import React, { useState, useRef, useEffect } from "react";
import Player from "react-player";
import ReactPlayer from "react-player";
import { Box, Typography } from "@material-ui/core";
import {
    ControlContainer,
    ControlIconButton,
    VideoInputRange,
    AudioInputRange,
    ControlButtonsContainer,
    ControlPlayIconButton,
} from "./AudioVideoPlayer.styled";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import PauseCircleFilledIcon from "@material-ui/icons/PauseCircleFilled";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import { toHHMMSS } from "utils";
import { useAnnotationsHandler } from "pages/MaterialSpecs/hooks";

interface AudioVideoPlayerProps {
    path: string;
    width?: number;
    height?: number;
    isAudio?: boolean;
    seekToSecond?: number;
    onReadyCallback?(videoLength: number): void;
    onProgressCallback?(playedSeconds: number): void;
}

export const AudioVideoPlayer = ({
    path,
    width,
    height,
    seekToSecond,
    onReadyCallback,
    onProgressCallback,
    isAudio = false,
}: AudioVideoPlayerProps) => {
    const { onSetVideoPlayer, isPlaying, onSetIsPlaying } = useAnnotationsHandler();
    const [muted, setMuted] = useState<boolean>(!isAudio);
    const [showControls, setShowControls] = useState<boolean>(false);
    const [playedTimeInSeconds, setPlayedTimeInSeconds] = useState<number>(0);
    const player = useRef<ReactPlayer>(null);

    useEffect(() => {
        onSetVideoPlayer(player.current);

        return () => {
            onSetVideoPlayer(null);
            onSetIsPlaying(false);
        };
    }, [onSetVideoPlayer, onSetIsPlaying]);

    useEffect(() => {
        if (player.current && seekToSecond) {
            player.current.seekTo(seekToSecond, "seconds");
            setPlayedTimeInSeconds(seekToSecond);
            onSetIsPlaying(false);
        }
    }, [seekToSecond, onSetIsPlaying]);

    useEffect(() => {
        setMuted(!isAudio);
    }, [isAudio]);

    const handleProgress = ({ playedSeconds }: { playedSeconds: number }) => {
        setPlayedTimeInSeconds(playedSeconds);
        if (onProgressCallback) {
            onProgressCallback(playedSeconds);
        }
    };

    const handleRangeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        player.current?.seekTo(parseFloat(event.target.value), "seconds");
        setPlayedTimeInSeconds(parseFloat(event.target.value));
    };

    return (
        <Box>
            <Player
                ref={player}
                onReady={() => {
                    if (onReadyCallback && player.current) {
                        onReadyCallback(player.current.getDuration());
                    }

                    setShowControls(true);
                }}
                progressInterval={1}
                playing={isPlaying}
                controls={false}
                url={path}
                muted={muted}
                width={isAudio ? 0 : width}
                height={isAudio ? 0 : height}
                onProgress={handleProgress}
                onEnded={() => onSetIsPlaying(false)}
            />

            {showControls && player.current && (
                <ControlContainer>
                    {isAudio ? (
                        <AudioInputRange
                            type="range"
                            onChange={handleRangeChange}
                            value={playedTimeInSeconds}
                            step={0.001}
                            min={0}
                            max={player.current.getDuration()}
                        />
                    ) : (
                        <VideoInputRange
                            type="range"
                            onChange={handleRangeChange}
                            value={playedTimeInSeconds}
                            step={0.001}
                            min={0}
                            max={player.current.getDuration()}
                        />
                    )}

                    <ControlButtonsContainer>
                        <ControlPlayIconButton disableRipple onClick={() => onSetIsPlaying(!isPlaying)}>
                            {isPlaying ? (
                                <PauseCircleFilledIcon color="primary" />
                            ) : (
                                <PlayCircleFilledIcon color="primary" />
                            )}
                        </ControlPlayIconButton>

                        <ControlIconButton disableRipple onClick={() => setMuted((isMuted) => !isMuted)}>
                            {muted ? <VolumeOffIcon color="primary" /> : <VolumeUpIcon color="primary" />}
                        </ControlIconButton>

                        <Box pr={1} pl={1}>
                            <Typography component="span" variant="body2">
                                {toHHMMSS(playedTimeInSeconds)}
                            </Typography>
                            <Typography component="span" variant="body2">
                                /
                            </Typography>
                            <Typography component="span" variant="body2">
                                {toHHMMSS(player.current.getDuration())}
                            </Typography>
                        </Box>
                    </ControlButtonsContainer>
                </ControlContainer>
            )}
        </Box>
    );
};
